import React from 'react';
import { Link2, Check, Download } from 'lucide-react';
import { formatEUR, priceLabel } from './lib';
import { AlternativePicker, RichText, TermsBlock, FeedbackBlock, QuestionBlock, AcceptanceBlock, FooterBlock, SectionsBlock, TotalsBlock, QuoteMetaBlock } from './SharedBlocks';

const OnepagerLayout = ({ ctx, theme }) => {
  const {
    quote, company, viewHash,
    einmaligServices, monatlichServices, jaehrlichServices,
    einmaligTotals, monatlichTotals, jaehrlichTotals,
    selectedAlternatives, selectAlternative, optionalServicesSelected, toggleOptionalService,
    lineTotal, handleSaveAsPdf, handleCopyLink, linkCopied, setShowAcceptanceForm,
  } = ctx;

  const cust = quote.customerData || {};
  const card = { background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, borderRadius: theme.radius, padding: '20px 22px' };
  const totalPositions = einmaligServices.length + monatlichServices.length + jaehrlichServices.length;

  const PriceCard = ({ label, value, suffix, highlight }) => (
    <div style={{ ...card, textAlign: 'center', border: highlight ? `2px solid ${theme.primary}` : card.border, flex: 1 }}>
      <p style={{ fontSize: 12, color: theme.textSecondary, margin: 0 }}>{label}</p>
      <p style={{ fontSize: 24, fontWeight: 700, color: highlight ? theme.primary : theme.textPrimary, margin: '6px 0 0' }}>{formatEUR(value)}</p>
      {suffix && <p style={{ fontSize: 11, color: theme.textMuted, margin: '2px 0 0' }}>{suffix}</p>}
    </div>
  );

  const Row = ({ service, color, suffix }) => {
    const isOpt = service.isOptional;
    const on = !isOpt || optionalServicesSelected[service.id];
    return (
      <div style={{ padding: '14px 0', borderBottom: `1px solid ${theme.surfaceBorder}`, opacity: isOpt && !on ? 0.55 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isOpt && <input type="checkbox" checked={!!optionalServicesSelected[service.id]} onChange={() => toggleOptionalService(service.id)} style={{ width: 17, height: 17, accentColor: theme.primary, cursor: 'pointer' }} />}
              <span style={{ width: 8, height: 8, borderRadius: 99, background: color }} />
              <p style={{ fontWeight: 600, fontSize: 14, color: theme.textPrimary, margin: 0 }}>{service.name}</p>
              {isOpt && <span style={{ fontSize: 11, color: theme.textMuted }}>(optional)</span>}
            </div>
            {service.description && <p style={{ fontSize: 13, color: theme.textSecondary, marginTop: 4, lineHeight: 1.6, paddingLeft: isOpt ? 25 : 16 }}>{service.description}</p>}
            <div style={{ paddingLeft: isOpt ? 25 : 16 }}>
              <AlternativePicker service={service} theme={theme} selectedAlternatives={selectedAlternatives} onSelect={selectAlternative} />
            </div>
          </div>
          <span style={{ fontSize: 15, fontWeight: 600, color: theme.textPrimary, whiteSpace: 'nowrap' }}>{priceLabel(service, lineTotal(service))}{suffix ? <span style={{ fontSize: 11, color: theme.textMuted }}> {suffix}</span> : null}</span>
        </div>
      </div>
    );
  };

  const Group = ({ title, services, color, suffix }) => services.length ? (
    <div style={card}>
      <h3 style={{ fontSize: 13, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color, marginTop: 0, marginBottom: 4 }}>{title}</h3>
      {services.map((s, i) => <Row key={s.id || i} service={s} color={color} suffix={suffix} />)}
    </div>
  ) : null;

  const headBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: theme.radius, background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, color: theme.textPrimary, cursor: 'pointer', fontSize: 13 };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: theme.font, color: theme.textPrimary, padding: '0 0 56px' }}>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 20px', maxWidth: 920, margin: '0 auto' }}>
        <button onClick={handleSaveAsPdf} style={headBtn}><Download className="w-4 h-4" />PDF</button>
        <button onClick={handleCopyLink} style={headBtn}>{linkCopied ? <Check className="w-4 h-4" style={{ color: '#22c55e' }} /> : <Link2 className="w-4 h-4" />}{linkCopied ? 'Kopiert' : 'Link'}</button>
      </div>

      {/* HERO centered */}
      <div style={{ textAlign: 'center', padding: '40px 24px 36px', maxWidth: 720, margin: '0 auto' }}>
        {company?.logo && <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo}`} alt="Logo" style={{ height: 38, marginBottom: 22, objectFit: 'contain' }} />}
        {cust.company && <p style={{ fontSize: 12, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: theme.primary, marginBottom: 12 }}>Angebot für {cust.company}</p>}
        <h1 style={{ fontSize: 32, lineHeight: 1.2, fontWeight: 700, margin: 0 }}>{quote.heroHeadline || quote.quoteTitle || 'Ihr digitales Fundament'}</h1>
        {quote.quoteIntro && <RichText value={quote.quoteIntro} style={{ fontSize: 15, color: theme.textSecondary, marginTop: 14, lineHeight: 1.7}} />}
        <button onClick={() => setShowAcceptanceForm(true)} className="no-print" style={{ marginTop: 22, padding: '13px 30px', borderRadius: theme.radius, background: theme.primary, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 500 }}>
          Angebot annehmen
        </button>
      </div>

      <div style={{ maxWidth: 920, margin: '0 auto', padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>

        {/* Stat row */}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ ...card, flex: 1, textAlign: 'center' }}>
            <p style={{ fontSize: 12, color: theme.textSecondary, margin: 0 }}>Positionen</p>
            <p style={{ fontSize: 22, fontWeight: 700, margin: '4px 0 0' }}>{totalPositions}</p>
          </div>
          <div style={{ ...card, flex: 1, textAlign: 'center' }}>
            <p style={{ fontSize: 12, color: theme.textSecondary, margin: 0 }}>Angebots-Nr.</p>
            <p style={{ fontSize: 16, fontWeight: 600, margin: '8px 0 0' }}>{quote.quoteNumber}</p>
          </div>
          <div style={{ ...card, flex: 1, textAlign: 'center' }}>
            <p style={{ fontSize: 12, color: theme.textSecondary, margin: 0 }}>Gültig bis</p>
            <p style={{ fontSize: 16, fontWeight: 600, margin: '8px 0 0' }}>{new Date(new Date(quote.createdAt).setDate(new Date(quote.createdAt).getDate() + 30)).toLocaleDateString('de-DE')}</p>
          </div>
        </div>

        {quote.videoUrl && (
          <div className="no-print" style={card}>
            <video src={quote.videoUrl} controls style={{ width: '100%', borderRadius: theme.radius, maxHeight: 420 }} />
          </div>
        )}

        {quote.customerArguments && (
          <div style={{ ...card, borderLeft: `4px solid ${theme.primary}` }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: theme.primary, marginTop: 0, marginBottom: 10 }}>Warum dieses Angebot für Sie?</h3>
            <div style={{ fontSize: 14, color: theme.textSecondary, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: quote.customerArguments }} />
          </div>
        )}

        {/* Pricing cards */}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {einmaligTotals.netto > 0 && <PriceCard label="Einmalig" value={einmaligTotals.netto} suffix="netto" />}
          {monatlichTotals.netto > 0 && <PriceCard label="Monatlich" value={monatlichTotals.netto} suffix="pro Monat, netto" highlight />}
          {jaehrlichTotals.netto > 0 && <PriceCard label="Jährlich" value={jaehrlichTotals.netto} suffix="pro Jahr, netto" />}
        </div>

        <QuoteMetaBlock quote={quote} theme={theme} company={company} />
        <Group title="Einmalige Leistungen" services={einmaligServices} color={theme.einmalig} suffix="" />
        <Group title="Monatliche Leistungen" services={monatlichServices} color={theme.monatlich} suffix="/ Monat" />
        <Group title="Jährliche Leistungen" services={jaehrlichServices} color={theme.jaehrlich} suffix="/ Jahr" />

        <TermsBlock quote={quote} company={company} theme={theme} />
        <TotalsBlock ctx={ctx} theme={theme} />
        <SectionsBlock quote={quote} theme={theme} ctx={ctx} />
        <AcceptanceBlock ctx={ctx} theme={theme} />
        <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FeedbackBlock viewHash={viewHash} theme={theme} />
          <QuestionBlock viewHash={viewHash} theme={theme} />
        </div>
        <FooterBlock company={company} theme={theme} />
      </div>
    </div>
  );
};

export default OnepagerLayout;
